import "../style.css";
import { PlusOutlined, SearchOutlined, SyncOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  PageHeader,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
  Row,
  Col,
} from "antd";
import React, { useState, useEffect } from "react";
import IconNotFound from "../../../static/icon/IconNotFound";
import ActionModal from "./ActionModal";
import { useAuth } from "../../../context/auth";
import DeleteAction from "../../../Tools/DeleteAction";
import PageDropdown from "../../Dropdown/PageDropdown";
import IconPrivacyPolicy from "../../../static/icon/IconPrivacyPolicy";

const { Text } = Typography;

const PrivacyPolicy = () => {
  const { accessToken } = useAuth();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState([]);
  const [search, setSearch] = useState({
    pageValue: {},
    searchValue: {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [modalType, setModalType] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);

  useEffect(() => {
    async function getPages({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/page', window.location);
      if (searchValue.id) url.searchParams.append('id', searchValue.id);
      url.searchParams.append('type', 'privacy');
      if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);
      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, pages } = await res.json();
        setTotal(total);
        setPages(pages);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getPages(search);
  }, [search, accessToken, update]);

  const dateFormatter = Intl.DateTimeFormat('th', { year: 'numeric', month: '2-digit', day: '2-digit' });

  const columns = [
    {
      title: "Title (EN)",
      dataIndex: "titleEn",
      key: "titleEn",
      width: "100px",
      sorter: () => null,
      render: (titleEn) => {
        return (
          <Text
            ellipsis={{ tooltip: titleEn }}
            style={{ width: 100 }}
            className="txt"
          >
            {titleEn || "-"}
          </Text>
        );
      },
    },
    {
      title: "Title (TH)",
      dataIndex: "titleTh",
      key: "titleTh",
      width: "100px",
      sorter: () => null,
      render: (titleTh) => {
        return (
          <Text
            ellipsis={{ tooltip: titleTh }}
            style={{ width: 100 }}
            className="txt"
          >
            {titleTh || "-"}
          </Text>
        );
      },
    },
    {
      title: "Update Date",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "100px",
      sorter: () => null,
      render: (updatedAt) => {
        return (
          <span
            className="font-base"
            style={{ color: "#495057" }}
          >
            {dateFormatter.format(new Date(updatedAt))}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: "80px",
      sorter: () => null,
      render: (active) => {
        return (
          <Tag
            className="p-1"
            style={{ width: 100, textAlign: "center" }}
            color={active ? "blue" : null}
          >
            {active ? "Published" : "Draft"}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: "80px",
      render: (_, page) => (
        <div className="flex space-x-2">
          <Tooltip
            className="cursor-pointer hover:opacity-80"
            placement="bottom"
            title="Edit"
            onClick={() => {
              setModalType("edit");
              setCurrentPage(page);
            }}
          >
            <svg
              width={32}
              height={32}
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width={32} height={32} rx={6} fill="#F3F6F9" />
              <path
                d="M22.1383 10.9863C22.7157 11.5637 22.7252 12.4969 22.1596 13.0859L16.7427 18.727C16.4674 19.0137 16.0896 19.1794 15.6922 19.1877L13.4169 19.2355L13.465 16.9449C13.4731 16.5566 13.6316 16.1865 13.907 15.9127L19.4441 10.4073C20.0303 9.82436 20.9777 9.82572 21.5623 10.4103L22.1383 10.9863Z"
                fill="#3699FF"
              />
              <path
                opacity="0.3"
                d="M16.6002 9.33337C16.9684 9.33337 17.2668 9.63185 17.2668 10C17.2668 10.3682 16.9684 10.6667 16.6002 10.6667H12.0002C11.2638 10.6667 10.6668 11.2637 10.6668 12V20C10.6668 20.7364 11.2638 21.3334 12.0002 21.3334H20.0002C20.7365 21.3334 21.3335 20.7364 21.3335 20V16.6667C21.3335 16.2985 21.632 16 22.0002 16C22.3684 16 22.6668 16.2985 22.6668 16.6667V20C22.6668 21.4728 21.4729 22.6667 20.0002 22.6667H12.0002C10.5274 22.6667 9.3335 21.4728 9.3335 20V12C9.3335 10.5273 10.5274 9.33337 12.0002 9.33337H16.6002Z"
                fill="#3699FF"
              />
            </svg>
          </Tooltip>
          {!page.active && (
            <Tooltip
              className="cursor-pointer hover:opacity-80"
              placement="bottom"
              title="Delete"
              onClick={() => {
                setModalType("delete");
                setCurrentPage(page);
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="32" height="32" rx="6" fill="#F3F6F9" />
                <path
                  d="M12 13.3334V21.1667C12 21.9951 12.6716 22.6667 13.5 22.6667H18.5C19.3284 22.6667 20 21.9951 20 21.1667V13.3334H12Z"
                  fill="#F64E60"
                />
                <path
                  opacity="0.3"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M17.3335 11C17.3335 10.4477 16.8858 10 16.3335 10H15.6668C15.1145 10 14.6668 10.4477 14.6668 11H11.8335C11.5574 11 11.3335 11.2239 11.3335 11.5C11.3335 11.7761 11.5574 12 11.8335 12H20.1668C20.443 12 20.6668 11.7761 20.6668 11.5C20.6668 11.2239 20.443 11 20.1668 11H17.3335Z"
                  fill="#F64E60"
                />
              </svg>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex items-center mt-2">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconPrivacyPolicy /> &nbsp;&nbsp;Privacy Policy
            </span>
          }
        />

        <Button
          className="mt-2 flex items-center"
          icon={<PlusOutlined />}
          type="primary"
          shape="round"
          onClick={() => {
            setModalType("new");
            setCurrentPage({});
          }}
        >
          New Policy
        </Button>
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={8}>
                <Form.Item
                  label="Title (EN/TH)"
                  name="id"
                  colon={false}
                  initialValue={null}
                >
                  <PageDropdown
                    update={update}
                    type="privacy"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Status"
                  name="active"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Status' },
                      { value: true, label: 'Active' },
                      { value: false, label: 'Inactive' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={6}>
                <div className="flex mt-3">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>
                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(page) => page.id}
          loading={loading}
          columns={columns}
          dataSource={pages}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <ActionModal
        visible={modalType !== 'delete' && !!currentPage}
        modalType={modalType}
        editRecord={currentPage}
        onCancel={() => setCurrentPage(null)}
        onOk={() => {
          setCurrentPage(null);
          setUpdate(update + 1);
        }}
      />

      <DeleteAction
        openModalActionDelete={modalType === 'delete' && !!currentPage}
        name="Privacy Policy"
        type="page"
        editRecord={currentPage}
        onCancel={() => setCurrentPage(null)}
        onOk={() => {
          setCurrentPage(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default PrivacyPolicy;
